import {createContext} from "react";
import Page from "../../api/Page";
import SimpleQRCode from "../../api/qr-code/SimpleQRCode";
import GetListQuery from "../../api/qr-code/GetListQuery";
import QRCode from "../../api/qr-code/QRCode";
import CreatePayload from "../../api/qr-code/CreatePayload";

/**
 * CreatePayload, but with optional key
 * @author Kacper Faber
 * @since 0.2.1
 */
export type CreatePayloadNoKey = Omit<CreatePayload, 'key'> & Partial<Pick<CreatePayload, 'key'>>;

/**
 * Defines type for QRCodeContext
 * @author Kacper Faber
 * @since 0.1.7
 */
type QRCodeContextType = {
    /**
     * Query
     * @author Kacper Faber
     * @since 0.1.7
     */
    query: GetListQuery;

    /**
     * Update query..
     * @author Kacper Faber
     * @since 0.1.7
     * @param query
     */
    setQuery: (query: GetListQuery) => void;

    /**
     * Data.
     * It's possibly undefined.
     * @author Kacper Faber
     * @since 0.1.7
     */
    page?: Page<SimpleQRCode>;

    /**
     * Is fetching?
     * @author Kacper Faber
     * @since 0.1.7
     */
    isFetching: boolean;

    /**
     * Get QR by ID.
     * @since 0.1.9
     * @author Kacper Faber
     */
    getById: (id: number | string) => Promise<QRCode>;

    /**
     * Reload the QRs in context.
     * @since 0.1.9
     * @author Kacper Faber
     */
    reload: () => void;

    deleteById: (id: number | string) => Promise<void>;

    /**
     * Create new QRCode and reloads the context.
     * @author Kacper Faber
     * @since 0.2.1
     */
    create: (payload: CreatePayloadNoKey) => void;
}

/**
 * Throws new error.
 * @author Kacper Faber
 * @since 0.1.7
 */
const notImplementedMethod = () => {
    throw new Error("Not Implemented");
}

/**
 * Default values for QRCodeContext.
 * Should be replaced in wrapper/provider, because defaults are not implemented.
 * @author Kacper Faber
 * @since 0.1.7
 */
const defaults: QRCodeContextType = {
    query: {},
    setQuery: notImplementedMethod,
    isFetching: false,
    getById: notImplementedMethod,
    reload: notImplementedMethod,
    deleteById: notImplementedMethod,
    create: notImplementedMethod
};

/**
 * QR code context.
 * Holds QRCodeView data
 * @author Kacper Faber
 * @since 0.1.7
 */
const qrCodeContext = createContext<QRCodeContextType>(defaults);

export default qrCodeContext;